import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * ימים מיוחדים: חגים שהגיעו מהסנכרון, וימים שהרשת הוסיפה בעצמה.
 *
 * חג הוא רק סוג אחד של יום מיוחד. בלאק פריידי או אירוע מקומי
 * משנים את הסידור בדיוק כמו סוכות.
 */
export type SpecialDay = {
  id: string;
  date: string;
  name: string;
  kind: 'erev' | 'holiday' | 'other';
  source: 'hebcal' | 'manual';
  /** הוסתר מהרשימה ומהלוח. נשמר גם אחרי סנכרון מחדש. */
  is_hidden: boolean;
};

/** מה הרשת סוגרת בערב חג ובחג. ניתן לשינוי בהגדרות. */
export type ClosureDefault = 'afternoon' | 'all_day' | 'none';

export function useSpecialDays(fromDate: string) {
  return useQuery({
    queryKey: ['special-days', fromDate] as const,
    queryFn: async (): Promise<SpecialDay[]> => {
      const { data, error } = await supabase
        .from('special_days')
        .select('id, date, name, kind, source, is_hidden')
        .gte('date', fromDate)
        .order('date')
        .limit(60);
      if (error) throw error;
      return (data ?? []) as SpecialDay[];
    },
  });
}

/**
 * ההצעה ליום אחד, לפי ברירות המחדל של הרשת.
 *
 * 'none' הוא תשובה לגיטימית: רשת שפותחת בערב חג לא אמורה לראות
 * כפתור סגירה בכל פעם.
 */
export function suggestionFor(
  day: SpecialDay,
  defaults: { erev: ClosureDefault; holiday: ClosureDefault },
): ClosureDefault {
  if (day.kind === 'erev') return defaults.erev;
  if (day.kind === 'holiday') return defaults.holiday;
  return 'none';
}

export const SUGGESTION_LABEL: Record<ClosureDefault, string> = {
  afternoon: 'סגירת צהריים',
  all_day: 'סגירת כל היום',
  none: 'בלי סגירה',
};

export function readDefault(raw: unknown, fallback: ClosureDefault): ClosureDefault {
  return raw === 'afternoon' || raw === 'all_day' || raw === 'none' ? raw : fallback;
}

/**
 * הימים המיוחדים של טווח תאריכים, לפי תאריך.
 *
 * ללוח השבועי: גם יום שהסניף פתוח בו הוא מידע שמשנה שיבוץ —
 * מי שיודע שזה ערב חג משבץ אחרת, גם בלי כלל סגירה.
 */
export function useSpecialDaysByDate(from: string, to: string) {
  return useQuery({
    queryKey: ['special-days-range', from, to] as const,
    queryFn: async (): Promise<Map<string, SpecialDay[]>> => {
      const { data, error } = await supabase
        .from('special_days')
        .select('id, date, name, kind, source, is_hidden')
        .gte('date', from)
        .lte('date', to)
        .eq('is_hidden', false)
        .order('date');
      if (error) throw error;

      const byDate = new Map<string, SpecialDay[]>();
      for (const row of (data ?? []) as SpecialDay[]) {
        byDate.set(row.date, [...(byDate.get(row.date) ?? []), row]);
      }
      return byDate;
    },
  });
}

/**
 * הסתרה והחזרה של יום מיוחד.
 *
 * הסתרה ולא מחיקה: שורה שהגיעה מ Hebcal נמחקת ונכתבת מחדש בכל
 * סנכרון, ולכן יום שנמחק היה חוזר מעצמו. חוץ מזה, "לא רוצה
 * לראות את חנוכה" אינו "חנוכה לא קיים", וההחלטה הפיכה.
 */
export function useSpecialDayMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['special-days'] });
    void queryClient.invalidateQueries({ queryKey: ['special-days-range'] });
  };

  const setHidden = useMutation({
    mutationFn: async (input: { id: string; hidden: boolean }) => {
      const { error } = await supabase
        .from('special_days')
        .update({ is_hidden: input.hidden })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { setHidden };
}
